import {IPlatformLocation} from '../../../../return-methods/return-method-types';
import {IMerchantFilteringExchangeLocation, REST_OF_WORLD} from '../../../../../models/User';
import {isSet} from '../../../../../models/model-types-methods';
import {ICheckboxesTreeItem} from '../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store';

/**
 * Stateless utility for converting between warehouse data and flat tree structure for CheckboxesTreeDropdown.
 * Responsibilities:
 * - Build a flat tree from warehouses (no nesting, list)
 * - Convert locations to checked tree
 * - Extract warehouse IDs from tree
 * - Build fallback locations (REST_OF_WORLD) for selected warehouses
 * - Provide O(1) lookups for warehouse names
 */
class WarehousesTreeConverter {

    private readonly _optionsTree: ICheckboxesTreeItem[] = [];
    private readonly _warehouseByIdMap: Map<string, IPlatformLocation> = new Map();

    /**
     * Initialize converter with warehouses and optional disabled IDs.
     * Builds and caches options tree with disabled flags applied.
     * Builds warehouse lookup map for O(1) name resolution.
     */
    constructor(
        private readonly _warehouses: IPlatformLocation[],
        disabledWarehouseIds?: Set<string>
    ) {
        this._warehouseByIdMap = this._buildWarehouseByIdMap();
        this._optionsTree = this._buildOptionsTree(disabledWarehouseIds);
    }

    /**
     * Get cached options tree for CheckboxesTreeDropdown.
     * Returns flat tree structure with disabled flags already applied.
     */
    public getOptionsTree(): ICheckboxesTreeItem[] {
        return this._optionsTree;
    }

    /**
     * Convert locations to checked tree structure for CheckboxesTreeDropdown.
     * Extracts unique warehouse IDs from locations and builds a flat tree.
     * Returns undefined for no selection, [] for select all, or tree for specific selection.
     */
    public buildValueTree(
        locations: IMerchantFilteringExchangeLocation[]
    ): ICheckboxesTreeItem[] | undefined {
        if (!isSet(locations) || locations.length === 0) {
            return undefined;
        }

        /** Extract unique warehouse IDs */
        const warehouseIds = new Set<string>();
        locations.forEach((location) => {
            warehouseIds.add(location.location_id);
        });

        /** Build a flat tree from warehouse IDs */
        const valueTree: ICheckboxesTreeItem[] = [];
        warehouseIds.forEach((warehouseId) => {
            const warehouse = this._warehouseByIdMap.get(warehouseId);
            const label = warehouse ? (warehouse.name ?? warehouseId) : warehouseId;

            valueTree.push({
                value: warehouseId,
                label,
            });
        });

        return valueTree;
    }

    /**
     * Extract warehouse IDs from tree selection.
     * Converts CheckboxesTreeDropdown value to array of warehouse location_ids.
     * Returns empty array if no selection.
     */
    public extractWarehouseIds(tree: ICheckboxesTreeItem[] | undefined): string[] {
        if (!isSet(tree) || tree.length === 0) {
            return [];
        }

        return tree.map((node) => node.value);
    }

    /**
     * Builds fallback locations with compressed mode support.
     * Compressed mode: if all warehouses selected, returns empty array (means "all").
     * Otherwise: returns REST_OF_WORLD locations for selected warehouses.
     */
    public buildFallbackLocations(warehouseIds: string[]): IMerchantFilteringExchangeLocation[] {
        const allWarehouseIds = this._warehouses.map((warehouse) => warehouse.location_id);

        const allSelected = allWarehouseIds.length === warehouseIds.length &&
            allWarehouseIds.every((warehouseId) => warehouseIds.includes(warehouseId));

        if (allSelected) {
            return [];
        }

        return warehouseIds.map((warehouseId) => ({
            location_id: warehouseId,
            predefined_country_group: REST_OF_WORLD,
            country_code: null,
        }));
    }

    /**
     * Build a warehouse lookup map for O(1) access.
     * Format: location_id → IPlatformLocation
     */
    private _buildWarehouseByIdMap(): Map<string, IPlatformLocation> {
        const warehouseMap = new Map<string, IPlatformLocation>();

        this._warehouses.forEach((warehouse) => {
            warehouseMap.set(warehouse.location_id, warehouse);
        });

        return warehouseMap;
    }

    /**
     * Build a flat options tree from warehouses with disabled flags applied.
     * No nesting - just a list of warehouse nodes.
     */
    private _buildOptionsTree(disabledWarehouseIds?: Set<string>): ICheckboxesTreeItem[] {
        return this._warehouses.map((warehouse) => {
            return {
                value: warehouse.location_id,
                label: warehouse.name ?? warehouse.location_id,
                disabled: disabledWarehouseIds ? disabledWarehouseIds.has(warehouse.location_id) : false,
            };
        });
    }
}

export default WarehousesTreeConverter;

